import { useEffect, useState } from 'react';
import { MapPin, CheckCircle2 } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Field } from '@/components/ui/Field';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { Button } from '@/components/ui/Button';
import { supabase } from '@/lib/supabaseClient';

export interface PathaoLocation {
  city_id: number;
  zone_id: number;
  area_id: number;
  city_name: string;
  zone_name: string;
  area_name: string;
}

interface LocationItem {
  id: number;
  name: string;
}

interface LocationHint {
  cityId?: number;
  zoneId?: number;
  areaId?: number;
}

function levenshtein(a: string, b: string): number {
  const dp: number[][] = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
  for (let i = 0; i <= a.length; i++) dp[i][0] = i;
  for (let j = 0; j <= b.length; j++) dp[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      dp[i][j] = a[i - 1] === b[j - 1] ? dp[i - 1][j - 1] : 1 + Math.min(dp[i - 1][j - 1], dp[i - 1][j], dp[i][j - 1]);
    }
  }
  return dp[a.length][b.length];
}

// Looks for `name` inside the free-text address, tolerating small spelling/
// transliteration differences (e.g. customer-typed "Khilgoan" vs Pathao's
// official "Khilgaon") — a strict substring check missed real addresses like
// that in practice, leaving zone/area unguessed even though the city guess
// (usually an exact match, e.g. "Dhaka") succeeded.
function fuzzyNameInAddress(addressLower: string, name: string): boolean {
  const clean = name.toLowerCase().trim();
  if (!clean) return false;
  if (addressLower.includes(clean)) return true;
  const maxDist = clean.length <= 4 ? 0 : clean.length <= 7 ? 1 : 2;
  const words = addressLower.split(/[^a-z]+/).filter(Boolean);
  const span = clean.split(/\s+/).length;
  for (let i = 0; i < words.length; i++) {
    const window = words.slice(i, i + span).join(' ');
    if (!window || Math.abs(window.length - clean.length) > maxDist + 2) continue;
    if (levenshtein(window, clean) <= maxDist) return true;
  }
  return false;
}

// Staff-facing cascading City → Zone → Area picker, backed by
// core-pathao-locations (which holds the Pathao credentials server-side).
// Needed because checkout only collects a free-text address, but Pathao's
// booking API requires their own structured location IDs.
//
// Two ways this now auto-fills instead of making staff pick by hand every
// time:
//  1. Phone history — core-book-shipment remembers the City/Zone/Area from
//     this phone's last successful Pathao booking (core_customer_pathao_locations).
//     A returning customer's picker comes in fully filled.
//  2. Address-text guess — for a first-time phone, each level is guessed by
//     checking whether that item's name appears in the free-text address
//     (chained: city guessed -> its zones loaded -> zone guessed from the
//     same text -> its areas loaded -> area guessed the same way). Zone/area
//     naming is less reliable than city, so this is best-effort, not exact.
// Either way the dropdowns stay fully editable — an auto-fill is a
// starting point for staff to confirm or correct, never a silent commit.
export function PathaoLocationModal({
  open,
  onClose,
  onSubmit,
  busy,
  customerName,
  customerPhone,
  customerAddress,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (location: PathaoLocation) => void;
  busy?: boolean;
  customerName?: string;
  customerPhone?: string;
  customerAddress?: string;
}) {
  const [cities, setCities] = useState<LocationItem[]>([]);
  const [zones, setZones] = useState<LocationItem[]>([]);
  const [areas, setAreas] = useState<LocationItem[]>([]);
  const [cityId, setCityId] = useState('');
  const [zoneId, setZoneId] = useState('');
  const [areaId, setAreaId] = useState('');
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingZones, setLoadingZones] = useState(false);
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [error, setError] = useState('');
  const [autoFilled, setAutoFilled] = useState<'history' | 'guess' | null>(null);

  const addressLower = (customerAddress ?? '').toLowerCase();

  const pickZone = async (id: string, hint?: LocationHint) => {
    setZoneId(id);
    setAreaId('');
    setAreas([]);
    if (!id) return;
    setLoadingAreas(true);
    const { data, error: err } = await supabase.functions.invoke('core-pathao-locations', { body: { level: 'area', zone_id: id } });
    setLoadingAreas(false);
    if (err || !data?.ok) {
      setError(data?.details ?? err?.message ?? 'Failed to load areas');
      return;
    }
    const items = (data.items ?? []).map((a: Record<string, unknown>) => ({ id: a.area_id as number, name: a.area_name as string }));
    setAreas(items);
    const historyMatch = hint?.areaId && items.find((a) => a.id === hint.areaId);
    const guessMatch = !historyMatch && addressLower ? items.find((a) => fuzzyNameInAddress(addressLower, a.name)) : undefined;
    const match = historyMatch ?? guessMatch;
    if (match) {
      setAreaId(String(match.id));
      if (!historyMatch) setAutoFilled('guess');
    }
  };

  const pickCity = async (id: string, hint?: LocationHint) => {
    setCityId(id);
    setZoneId('');
    setAreaId('');
    setAreas([]);
    setZones([]);
    if (!id) return;
    setLoadingZones(true);
    const { data, error: err } = await supabase.functions.invoke('core-pathao-locations', { body: { level: 'zone', city_id: id } });
    setLoadingZones(false);
    if (err || !data?.ok) {
      setError(data?.details ?? err?.message ?? 'Failed to load zones');
      return;
    }
    const items = (data.items ?? []).map((z: Record<string, unknown>) => ({ id: z.zone_id as number, name: z.zone_name as string }));
    setZones(items);
    const historyMatch = hint?.zoneId && items.find((z) => z.id === hint.zoneId);
    const guessMatch = !historyMatch && addressLower ? items.find((z) => fuzzyNameInAddress(addressLower, z.name)) : undefined;
    const match = historyMatch ?? guessMatch;
    if (match) pickZone(String(match.id), hint);
  };

  useEffect(() => {
    if (!open) return;
    setCityId('');
    setZoneId('');
    setAreaId('');
    setZones([]);
    setAreas([]);
    setError('');
    setAutoFilled(null);
    setLoadingCities(true);
    (async () => {
      // A returning customer's last successful Pathao pick beats guessing
      // from text — it's a real, previously-confirmed delivery location.
      let hint: LocationHint | undefined;
      if (customerPhone) {
        const { data: historyRow } = await supabase
          .from('core_customer_pathao_locations')
          .select('city_id, zone_id, area_id')
          .eq('phone', customerPhone.trim())
          .maybeSingle();
        if (historyRow) {
          hint = { cityId: historyRow.city_id, zoneId: historyRow.zone_id, areaId: historyRow.area_id };
          setAutoFilled('history');
        }
      }

      const { data, error: err } = await supabase.functions.invoke('core-pathao-locations', { body: { level: 'city' } });
      setLoadingCities(false);
      if (err || !data?.ok) {
        setError(data?.details ?? err?.message ?? 'Failed to load cities');
        return;
      }
      const items = (data.items ?? []).map((c: Record<string, unknown>) => ({ id: c.city_id as number, name: c.city_name as string }));
      setCities(items);
      const historyMatch = hint?.cityId && items.find((c) => c.id === hint.cityId);
      const guessMatch = !historyMatch && addressLower ? items.find((c) => fuzzyNameInAddress(addressLower, c.name)) : undefined;
      const match = historyMatch ?? guessMatch;
      if (match) {
        if (!historyMatch) setAutoFilled('guess');
        pickCity(String(match.id), hint);
      } else {
        setAutoFilled(null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = () => {
    if (!cityId || !zoneId || !areaId) return;
    onSubmit({
      city_id: Number(cityId),
      zone_id: Number(zoneId),
      area_id: Number(areaId),
      city_name: cities.find((c) => String(c.id) === cityId)?.name ?? '',
      zone_name: zones.find((z) => String(z.id) === zoneId)?.name ?? '',
      area_name: areas.find((a) => String(a.id) === areaId)?.name ?? '',
    });
  };

  return (
    <Modal open={open} onClose={onClose} title="Select Delivery Area" subtitle="Pathao needs this to book the courier" icon={<MapPin className="w-4 h-4" />} busy={busy} maxWidth="max-w-md">
      <div className="space-y-3">
        {(customerName || customerPhone || customerAddress) && (
          <div className="rounded-lg border border-border-subtle p-3 space-y-0.5 bg-surface-muted">
            <p className="text-[10.5px] font-bold uppercase tracking-wide text-ink-faint">Order Address</p>
            {customerName && <p className="text-[12.5px] font-semibold text-ink">{customerName}</p>}
            {customerPhone && <p className="text-[12px] text-ink-faint">{customerPhone}</p>}
            {customerAddress && <p className="text-[12px] text-ink-faint">{customerAddress}</p>}
          </div>
        )}
        {autoFilled === 'history' && (
          <p className="flex items-center gap-1.5 text-[11px] font-semibold text-success">
            <CheckCircle2 className="w-3.5 h-3.5" /> Auto-filled from this customer's last Pathao delivery — check it's still correct
          </p>
        )}
        {autoFilled === 'guess' && (
          <p className="flex items-center gap-1.5 text-[11px] font-semibold text-primary-700">
            <CheckCircle2 className="w-3.5 h-3.5" /> Guessed from the address — please confirm before booking
          </p>
        )}
        {error && <p className="text-[12px] text-error bg-error-bg rounded-lg px-3 py-2">{error}</p>}
        <Field label="City">
          <SearchableSelect
            items={cities}
            value={cityId}
            onChange={(id) => { setAutoFilled(null); pickCity(id); }}
            placeholder="Select city…"
            loading={loadingCities}
          />
        </Field>
        <Field label="Zone">
          <SearchableSelect
            items={zones}
            value={zoneId}
            onChange={(id) => { setAutoFilled(null); pickZone(id); }}
            placeholder="Select zone…"
            disabled={!cityId}
            loading={loadingZones}
          />
        </Field>
        <Field label="Area">
          <SearchableSelect
            items={areas}
            value={areaId}
            onChange={(id) => { setAutoFilled(null); setAreaId(id); }}
            placeholder="Select area…"
            disabled={!zoneId}
            loading={loadingAreas}
          />
        </Field>
        <Button className="w-full" disabled={!areaId} loading={busy} onClick={handleSubmit}>
          Confirm &amp; Book with Pathao
        </Button>
      </div>
    </Modal>
  );
}
